import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { InputAdornment } from '@mui/material';
import { StyledContainer, StyledSearch, StyledClearIcon, StyledSearchIcon } from './styled';

export const ENTER = 13;

const SearchInput = ({
  placeholder = 'Search',
  value: controlledValue,
  onChange: controlledOnChange,
  onSearch,
  onClear,
  width = 170,
  testId,
  debounceInterval = 300,
  isInvalid,
  inputProps,
  helperText,
  ...restProps
}) => {
  const [value, setValue] = useState(controlledValue);
  useEffect(() => {
    setValue(controlledValue);
  }, [controlledValue]);

  const debounced = useMemo(() => debounce(e => controlledOnChange?.(e), debounceInterval), [controlledOnChange]);

  const onChange = e => {
    setValue(e.target.value);
    if (!controlledOnChange) return;
    debounceInterval ? debounced(e) : controlledOnChange(e);
  };

  const keyPress = e => {
    if (isInvalid || !onSearch) return;
    if (e.keyCode === ENTER) {
      onSearch(e.target.value);
    }
  };

  const searchHandler = () => {
    if (isInvalid) return;
    onSearch(value);
  };

  return (
    <StyledContainer>
      <StyledSearch
        id="outlined-search"
        data-testid={testId}
        placeholder={placeholder}
        variant="outlined"
        onChange={onChange}
        onKeyDown={keyPress}
        autoComplete="off"
        value={value}
        helperText={helperText}
        $error={isInvalid}
        $width={width}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {!!onSearch && <StyledSearchIcon onClick={searchHandler} />}
            </InputAdornment>
          ),
          endAdornment: inputProps?.endAdornment ? (
            inputProps.endAdornment
          ) : (
            <InputAdornment position="start">
              {!!onClear && <StyledClearIcon onClick={onClear} $visible={!!value} />}
            </InputAdornment>
          )
        }}
        {...restProps}
      />
    </StyledContainer>
  );
};

SearchInput.propTypes = {
  placeholder: PropTypes.string,
  testId: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
  isInvalid: PropTypes.bool,
  onSearch: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  inputProps: PropTypes.object,
  debounceInterval: PropTypes.number,
  overrideCss: PropTypes.object,
  width: PropTypes.number,
  helperText: PropTypes.string
};

export default SearchInput;
